import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';

const CartContext = createContext();

export const MAX_CART_QUANTITY = 99;

// A cart line always holds at least one piece; use removeFromCart to drop a line.
const clampQuantity = (value) => {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(Math.max(parsed, 1), MAX_CART_QUANTITY);
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    if (localStorage.getItem('token')) return [];
    const data = localStorage.getItem('guest_cart');
    return data ? JSON.parse(data) : [];
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  // Helper to load cart from localStorage for guests
  const loadGuestCart = () => {
    try {
      const data = localStorage.getItem('guest_cart');
      const items = data ? JSON.parse(data) : [];
      // Repairs guest carts stored before quantities were validated.
      return items.map(item => ({ ...item, quantity: clampQuantity(item.quantity) }));
    } catch {
      return [];
    }
  };
  // Helper to save cart to localStorage for guests
  const saveGuestCart = (items) => {
    localStorage.setItem('guest_cart', JSON.stringify(items));
  };

  // On mount or token change, load cart from backend or localStorage
  useEffect(() => {
    const fetchCart = async () => {
      if (!token) {
        const guestCart = loadGuestCart();
        setCartItems(guestCart);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        // If guest cart exists, sync to backend then clear
        const guestCart = loadGuestCart();
        if (guestCart.length > 0) {
          for (const item of guestCart) {
            await fetch(getApiUrl('/api/cart'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                product: item.product._id || item.product.id,
                quantity: item.quantity,
                size: item.size,
                color: item.color
              })
            });
          }
          localStorage.removeItem('guest_cart');
        }
        const res = await fetch(getApiUrl('/api/cart'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const err = await res.json();
          setError(err.message || 'Failed to fetch cart');
          return;
        }
        const data = await res.json();
        setCartItems(Array.isArray(data.items) ? data.items : []);
      } catch (error) {
        setError('Network error while fetching cart');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCart();
  }, [token]);

  const addToCart = async (product, requestedQuantity = 1, selectedSize = '', selectedColor = 'Default') => {
    const quantity = clampQuantity(requestedQuantity);

    if (!token) {
      // Guest: update localStorage
      let guestCart = loadGuestCart();
      const idx = guestCart.findIndex(
        item => (item.product._id || item.product.id) === (product._id || product.id) && item.size === selectedSize && item.color === selectedColor
      );
      if (idx > -1) {
        guestCart[idx].quantity = clampQuantity(clampQuantity(guestCart[idx].quantity) + quantity);
      } else {
        guestCart.push({ product, quantity, size: selectedSize, color: selectedColor });
      }
      saveGuestCart(guestCart);
      setCartItems(guestCart);
      return guestCart;
    }

    setError(null);
    try {
      const res = await fetch(getApiUrl('/api/cart'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product: product._id || product.id,
          quantity,
          size: selectedSize,
          color: selectedColor
        })
      });
      if (!res.ok) {
        const err = await res.json();
        console.error('Cart API error (add):', err);
        setError(err.message || 'Failed to add item to cart');
        return;
      }
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      setCartItems(newItems);
      return newItems;
    } catch (error) {
      console.error('Network error (addToCart):', error);
      setError('Network error while adding to cart');
      return null;
    }
  };

  const getCartCount = () => {
    return Array.isArray(cartItems) ? cartItems.length : 0;
  };

  const isInCart = (productId) => {
    return cartItems.some(item => (item.product?._id || item.product?.id) === productId);
  };

  const updateQuantity = async (productId, newQuantity, selectedSize, selectedColor) => {
    const quantity = clampQuantity(newQuantity);

    if (!token) {
      let guestCart = loadGuestCart();
      const idx = guestCart.findIndex(
        item => (item.product._id || item.product.id) === productId && item.size === selectedSize && item.color === selectedColor
      );
      if (idx > -1) {
        guestCart[idx].quantity = quantity;
      }
      saveGuestCart(guestCart);
      setCartItems(guestCart);
      return guestCart;
    }

    setError(null);
    try {
      const res = await fetch(getApiUrl(`/api/cart/${productId}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          quantity,
          size: selectedSize,
          color: selectedColor
        })
      });
      if (!res.ok) {
        const err = await res.json();
        console.error('Cart API error (update):', err);
        setError(err.message || 'Failed to update cart');
        return;
      }
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      setCartItems(newItems);
      return newItems;
    } catch (error) {
      console.error('Network error (updateQuantity):', error);
      setError('Network error while updating cart');
      return null;
    }
  };

  const removeFromCart = async (itemId) => {
    if (!token) {
      let guestCart = loadGuestCart();
      guestCart = guestCart.filter(item => item._id !== itemId && (item.product._id || item.product.id) !== itemId);
      saveGuestCart(guestCart);
      setCartItems(guestCart);
      return guestCart;
    }

    setError(null);
    try {
      const res = await fetch(getApiUrl(`/api/cart/${itemId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        console.error('Cart API error (remove):', err);
        setError(err.message || 'Failed to remove item from cart');
        return;
      }
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      setCartItems(newItems);
      return newItems;
    } catch (error) {
      console.error('Network error (removeFromCart):', error);
      setError('Network error while removing from cart');
      return null;
    }
  };

  // Function to clear the cart
  const clearCart = () => {
    if (!token) {
      localStorage.removeItem('guest_cart');
      setCartItems([]);
      return;
    }
    setCartItems([]);
  };

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      addToCart, 
      getCartCount, 
      isInCart, 
      updateQuantity, 
      removeFromCart,
      clearCart,
      isLoading,
      error
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 